import { useCallback, useRef } from 'react';

import type { AccountDetailsState } from '../signupUtils';
import type { ToggleAuthConnectionOptions } from './useAuthConnections';
import type { Region } from './useCreateAccount';
import type { SignupInteractionEvent } from './useSignupTracking';

type UseSignupFormInteractionsOptions = {
  applyDisplayNameChange: (value: string) => void;
  applyAuthConnectionToggle: (options: ToggleAuthConnectionOptions) => void;
  applyAcceptMarketingEmailsChange: (checked: boolean) => void;
  setField: (data: Partial<AccountDetailsState>) => void;
  trackInteraction: (interaction: SignupInteractionEvent) => void;
};

export const useSignupFormInteractions = ({
  applyDisplayNameChange,
  applyAuthConnectionToggle,
  applyAcceptMarketingEmailsChange,
  setField,
  trackInteraction,
}: UseSignupFormInteractionsOptions) => {
  const hasTrackedNameInputRef = useRef(false);
  const handleDisplayNameChange = useCallback(
    (value: string) => {
      if (!hasTrackedNameInputRef.current && value.trim() !== '') {
        hasTrackedNameInputRef.current = true;
        trackInteraction({
          control_id: 'workspace_name',
          control_type: 'text_input',
        });
      }
      applyDisplayNameChange(value);
    },
    [applyDisplayNameChange, trackInteraction],
  );

  const handleRegionChange = useCallback(
    (region: Region) => {
      trackInteraction({
        control_id: 'storage_region',
        control_type: 'radio',
        value: region,
      });
      setField({ region });
    },
    [setField, trackInteraction],
  );

  const toggleAuthConnection = useCallback(
    ({ connection, checked }: ToggleAuthConnectionOptions) => {
      trackInteraction({
        control_id: 'auth_connection',
        control_type: 'toggle_checkbox',
        value: connection,
        checked,
      });
      applyAuthConnectionToggle({ connection, checked });
    },
    [applyAuthConnectionToggle, trackInteraction],
  );

  const handleAcceptMarketingEmailsChange = useCallback(
    (checked: boolean) => {
      trackInteraction({
        control_id: 'marketing_emails',
        control_type: 'toggle_checkbox',
        checked,
      });
      applyAcceptMarketingEmailsChange(checked);
    },
    [applyAcceptMarketingEmailsChange, trackInteraction],
  );

  return {
    handleAcceptMarketingEmailsChange,
    handleDisplayNameChange,
    handleRegionChange,
    toggleAuthConnection,
  };
};
